import React, { useEffect, useState } from 'react';
import api from '@/utils/api';
import { Users, Search, Shield, User, Trash2, Edit2, Mail, Calendar, ShoppingBag, DollarSign, Plus, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { getPasswordComplexityErrors, PASSWORD_HINT } from '@/utils/passwordValidation';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', role: '', pro_shop_credit: 0 });
  const [saving, setSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', role: 'customer' });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadUsers();
    loadStats();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (roleFilter && roleFilter !== 'all') params.append('role', roleFilter);
      
      const response = await api.get(`/admin/users?${params.toString()}`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/users/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load user stats');
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      loadUsers();
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery, roleFilter]);

  const handleEditUser = async (user) => {
    try {
      const response = await api.get(`/admin/users/${user.user_id}`);
      setSelectedUser(response.data);
      setEditForm({
        name: response.data.name,
        role: response.data.role,
        pro_shop_credit: Number(response.data.pro_shop_credit || 0),
      });
      setEditDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      // Coerce credit to a number (input fields surface strings). Only send
      // pro_shop_credit for customer users — admins never redeem credit.
      const payload = {
        name: editForm.name,
        role: editForm.role,
      };
      if (editForm.role === 'customer') {
        payload.pro_shop_credit = Number(editForm.pro_shop_credit) || 0;
      }
      await api.put(`/admin/users/${selectedUser.user_id}`, payload);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      loadUsers();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await api.delete(`/admin/users/${selectedUser.user_id}`);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleAddUser = async () => {
    if (!addForm.name.trim() || !addForm.email.trim() || !addForm.password) {
      toast.error('Please fill in all fields');
      return;
    }
    const pwErrors = getPasswordComplexityErrors(addForm.password);
    if (pwErrors.length > 0) {
      toast.error(`Password must contain ${pwErrors.join(', ')}.`);
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/users', addForm);
      toast.success('User created successfully');
      setAddDialogOpen(false);
      setAddForm({ name: '', email: '', password: '', role: 'customer' });
      setShowPassword(false);
      loadUsers();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="text-center py-12" data-testid="users-loading">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading users...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          User Management
        </h2>
        <Button
          onClick={() => { setAddForm({ name: '', email: '', password: '', role: 'customer' }); setShowPassword(false); setAddDialogOpen(true); }}
          className="bg-[#4A6741] hover:bg-[#3d5636] text-white"
          data-testid="add-user-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_users}</p>
              </div>
              <Users className="h-8 w-8 text-[#4A6741]" />
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Admins</p>
                <p className="text-2xl font-bold text-gray-900">{stats.admin_count}</p>
              </div>
              <Shield className="h-8 w-8 text-[#D4A55A]" />
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Customers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.customer_count}</p>
              </div>
              <User className="h-8 w-8 text-[#334155]" />
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">New (30d)</p>
                <p className="text-2xl font-bold text-gray-900">{stats.new_users_30d}</p>
              </div>
              <Calendar className="h-8 w-8 text-[#4A6741]" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-gray-50 border-[#F3F4F6]"
            data-testid="user-search-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px] bg-gray-50 border-[#F3F4F6]" data-testid="role-filter-select">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
            <SelectItem value="customer">Customers</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <div className="bg-gray-50 rounded-lg border border-[#F3F4F6] overflow-hidden">
        <table className="w-full" data-testid="users-table">
          <thead className="bg-gray-100 border-b border-[#F3F4F6]">
            <tr>
              <th className="text-left p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">User</th>
              <th className="text-left p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">Role</th>
              <th className="text-left p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">Status</th>
              <th className="text-left p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">Joined</th>
              <th className="text-right p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="5" className="p-8 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.user_id} className="border-b border-[#F3F4F6] hover:bg-gray-100 transition-colors" data-testid={`user-row-${user.user_id}`}>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#4A6741] flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-900 font-medium">{user.name}</p>
                        <p className="text-gray-500 text-sm flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                      user.role === 'admin' 
                        ? 'bg-[#D4A55A]/10 text-[#D4A55A]' 
                        : 'bg-[#334155]/10 text-[#334155]'
                    }`}>
                      {user.role === 'admin' && <Shield className="h-3 w-3 mr-1" />}
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`inline-block px-2 py-1 rounded-lg text-xs font-bold ${
                      user.is_verified !== false
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {user.is_verified !== false ? 'Verified' : 'Pending'}
                    </span>
                  </td>
                  <td className="p-4 text-gray-600 text-sm">
                    {new Date(user.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="p-2 text-[#334155] hover:bg-gray-200 rounded-lg transition-colors"
                        title="Edit user"
                        data-testid={`edit-user-${user.user_id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => confirmDelete(user)}
                        className="p-2 text-[#EF4444] hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete user"
                        data-testid={`delete-user-${user.user_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-[#4A6741] flex items-center justify-center">
                  <span className="text-white font-bold">
                    {selectedUser.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{selectedUser.email}</p>
                  {selectedUser.order_count !== undefined && (
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <ShoppingBag className="h-3 w-3 mr-1" />
                        {selectedUser.order_count} orders
                      </span>
                      <span className="flex items-center">
                        <DollarSign className="h-3 w-3 mr-1" />
                        ${selectedUser.total_spent?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-gray-50 border-[#F3F4F6]"
                  data-testid="edit-user-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={editForm.role} onValueChange={(value) => setEditForm(prev => ({ ...prev, role: value }))}>
                  <SelectTrigger className="bg-gray-50 border-[#F3F4F6]" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editForm.role === 'customer' && (
                <div className="space-y-2">
                  <Label htmlFor="pro_shop_credit">Pro Shop Credit (USD)</Label>
                  <Input
                    id="pro_shop_credit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.pro_shop_credit}
                    onChange={(e) => setEditForm(prev => ({ ...prev, pro_shop_credit: e.target.value }))}
                    className="bg-gray-50 border-[#F3F4F6]"
                    data-testid="edit-user-pro-shop-credit"
                  />
                  <p className="text-xs text-gray-500">
                    Credit this customer can apply at checkout. The tenant is billed
                    separately each time it&apos;s redeemed.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveUser} 
              disabled={saving}
              className="bg-[#4A6741] hover:bg-[#3d5636]"
              data-testid="save-user-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
            </p>
            <p className="text-sm text-red-500 mt-2">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser}
              disabled={saving}
              data-testid="confirm-delete-user-btn"
            >
              {saving ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="add-name">Name</Label>
              <Input
                id="add-name"
                placeholder="Full name"
                value={addForm.name}
                onChange={(e) => setAddForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-gray-50 border-[#F3F4F6]"
                data-testid="add-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-email">Email</Label>
              <Input
                id="add-email"
                type="email"
                placeholder="user@example.com"
                value={addForm.email}
                onChange={(e) => setAddForm(prev => ({ ...prev, email: e.target.value }))}
                className="bg-gray-50 border-[#F3F4F6]"
                data-testid="add-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-password">Password</Label>
              <div className="relative">
                <Input
                  id="add-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 8 chars, upper/lower/number/symbol"
                  value={addForm.password}
                  onChange={(e) => setAddForm(prev => ({ ...prev, password: e.target.value }))}
                  className="bg-gray-50 border-[#F3F4F6] pr-10"
                  data-testid="add-user-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-role">Role</Label>
              <Select value={addForm.role} onValueChange={(value) => setAddForm(prev => ({ ...prev, role: value }))}>
                <SelectTrigger className="bg-gray-50 border-[#F3F4F6]" data-testid="add-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAddUser}
              disabled={saving}
              className="bg-[#4A6741] hover:bg-[#3d5636]"
              data-testid="confirm-add-user-btn"
            >
              {saving ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
